import React, { useEffect, useState } from 'react';
import { Route, Switch, Redirect, withRouter } from "react-router-dom";
import { IntlProvider, injectIntl } from "react-intl";
import styled, { ThemeProvider } from 'styled-components';
import AppService, { Utilities } from "s2s-app-service";
import { AlertIconSVG } from 's2s-svg-icons';
import Button from 's2s-button';
import { colorGrid } from 's2s-themes';
import AlertList from '../components/alertList';
import AppearanceSettings from '../components/appearanceSettings';
import SettingsNav from '../components/settingsnav';
import NotificationSettings from '../components/notificationSettings';
import TroubleshootingSettings from '../components/troubleshootingSettings';
import DeviceSettings from '../components/deviceSettings';
import ContactSettings from '../components/contactSettings';
import { getIntlText } from '../js/utilities';
import PropTypes from 'prop-types';
import { Alerts, Login, Connect, ForgotPw, Settings, CallProgress, Contacts, History, Update, Contact, ChangePassword, ContactEdit, Help } from "./index";
import useLocale from '../hooks/locale';
import useAuth from "../hooks/auth";
import Header from "../components/header";
import IncomingCall from "../components/incomingCall";
import useCallInfo from "../hooks/callInfo";
import useAlerts from '../hooks/alerts';
import useSettings from '../hooks/settings';
import useContacts from '../hooks/contacts';
import useCallHistory from '../hooks/callHistory';

const THEME_DARK = {
  background: '#121212',
  foreground: colorGrid.gray0,
  color: colorGrid.gray0,
  subtitle: {
    color: 'rgba(255, 255, 255, 0.74)',
  },
  button: {
    default: {
      background: '#9CA7E2',
      color: '#121212',
    },
    secondary: {
      color: '#9CA7E2',
      background: '#121212',
      borderColor: 'rgba(255, 255, 255, 0.16)',
    },
    flat: {
      subtitle: {
        color: 'rgba(255, 255, 255, 0.74)',
        fill: 'rgba(255, 255, 255, 0.74)',
      },
    },
    defaultHover: {
      background: colorGrid.indigo2,
      borderColor: colorGrid.gray5,
    },
    inverted: {
      background: colorGrid.gray8,
      color: colorGrid.gray0,
      borderColor: colorGrid.gray7,
    },
    invertedHover: {
      background: colorGrid.gray6,
    },
    splitter: {
      color: 'rgba(255, 255, 255, 0.16)',
    },
    iconButton: {
      background: '#6cdbe9',
      color: '#0f1c1e',
    },
  },
  selectInput: {
    color: colorGrid.gray0,
    borderColor: 'rgba(255, 255, 255, 0.16)',
    background: '#9CA7E2',
    menuList: {
      background: '#2E2E2E',
      borderColor: 'rgba(255, 255, 255, 0.16)',
      hovered: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: colorGrid.gray0,
      },
    },
  },
  link: {
    color: colorGrid.gray4,
  },
  input: {
    default: {
      color: colorGrid.gray0,
      hintColor: 'rgba(255, 255, 255, 0.74)',
      borderColor: 'rgba(255, 255, 255, 0.16)',

    },
    focused: {
      color: colorGrid.gray0,
      hintColor: '#9CA7E2',
      borderColor: '#9CA7E2',
    },
    error: {
      color: colorGrid.red3,
    },
  },
  searchInput: {
    borderColor: '#2E2E2E',
    color: 'rgba(255, 255, 255, 0.74)',
    addBtn: {
      boxShadow: 'none',
    }
  },
  contacts: {
    searchInput: {
      borderColor: '#DADADA',
      borderFocusColor: '#9CA7E2'
    },
    contactLabel: {
      background: '#2E2E2E',
      color: '#fff',
      fontColor: colorGrid.gray0,
      subFontColor: 'rgba(255, 255, 255, 0.74)',
      icon: {
        fill: '#38b24d',
      },
      hover: {
        background: '#2e2e2e',
      },
      arrow: {
        fill: colorGrid.gray0,
      },
    },
    buttons: {
      default: {
        container: {
          border: 'rgba(255, 255, 255, 0.16)',
        },
        save: {
          color: '#121212',
          background: '#9CA7E2'
        },
        cancel: {
          color: colorGrid.gray0,
          border: 'rgba(255, 255, 255, 0.16)'
        }
      }
    },
    link: {
      color: '#9CA7E2'
    }
  },
  checkbox: {
    background: colorGrid.gray8,
    borderColor: 'rgba(255, 255, 255, 0.74)',
    checked: {
      background: '#9CA7E2',
      borderColor: '#9CA7E2',
    },
  },
  icon: {
    fill: 'rgba(255, 255, 255, 0.74)',
    logoIcon: {
      fill: 'rgba(255, 255, 255, 0.38)',
    }
  },
  dialpad: {
    default: {
      background: '#121212',
      borderColor: 'rgba(255, 255, 255, 0.16)',
      iconColor: 'rgba(255, 255, 255, 0.74)',
      color: colorGrid.gray0
    },
    item: {
      background: '#2E2E2E',
      color: '#9CA7E2',
      colorDisabled: colorGrid.gray9,
      colorActive: '#F28B82',
      backgroundHover: '#2b2b2b',
      backgroundPressed: '#212121',
      greenBackground: '#2f9e44',
      greenBackgroundHover: '#37B24D',
      redBackground: '#f24e41',
      redBackgroundHover: '#EA3D2F',
    },
    audioSettings: {
      heading: {
        color: 'rgba(255, 255, 255, 0.74)'
      },
      button: {
        color1: 'rgba(255, 255, 255, 0.16)',
        color2: colorGrid.gray0,
        color3: '#121212',
        color4: '#9CA7E2'
      }
    },
    callDetail: {
      color1: colorGrid.gray0,
      color2: 'rgba(255, 255, 255, 0.74)'
    },
    incomingCall: {
      borderColor: 'rgba(255, 255, 255, 0.16)',
      color: '#9CA7E2',
      fontColor: colorGrid.gray0
    }
  },
  userControls: {
    color: 'rgba(255, 255, 255, 0.74)',
    defaultHover: {
      background: '#2E2E2E',
    },
  },
  header: {
    background: '#2E2E2E',
    fill: colorGrid.gray0,
    color: colorGrid.gray0,
    hover: {
      color: colorGrid.gray2,
      fill: colorGrid.gray2,
    },
    subheader: {
      background: '#383838',
    },
    button: {
      default: {
        background: colorGrid.gray6,
        color: colorGrid.gray5,
      },
      hover: {
        background: 'rgba(255, 255, 255, 0.1)',
      },
    },
    menuItem: {
      color: colorGrid.gray3,
      background: colorGrid.gray7,
      borderColor: colorGrid.gray6,

      hovered: {
        color: colorGrid.gray3,
        background: colorGrid.gray6,
      },
    },
  },
  alerts: {
    info: {
      background: '#084c14',
      color: '#daf2de'
    },
    warn: {
      background: '#993d00',
      color: '#f1e0cb'
    },
    error: {
      background: '#991a0f',
      color: '#ffe7e5'
    },
    listSection: {
      background: colorGrid.gray7,
      borderColor: 'rgba(255, 255, 255, 0.16)',
      markers: {
        info: {
          background: '#37B24D',
        },
        warn: {
          background: '#F3AA18',
        },
        error: {
          background: '#EA3D2F',
        },
      }
    }
  },
  history: {
    default: {
      color: colorGrid.gray0,
      hoverColor: '#2E2E2E',
      totalColor: 'rgba(255, 255, 255, 0.74)',
      borderColor: 'rgba(255, 255, 255, 0.16)',
      background: '#121212',
      iconRegularColor: 'rgba(255, 255, 255, 0.74)',
      iconErrorColor: colorGrid.red7,
    }
  }
};


const THEME_LIGHT = {
  background: colorGrid.gray0,
  foreground: '#304DC5',
  color: 'rgba(0, 0, 0, 0.87)',
  subtitle: {
    color: 'rgba(0, 0, 0, 0.6)',
  },
  button: {
    default: {
      background: '#304DC5',
      color: colorGrid.gray0,
    },
    secondary: {
      color: '#304DC5',
      background: colorGrid.gray0,
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    flat: {
      subtitle: {
        color: 'rgba(0, 0, 0, 0.6)',
        fill: 'rgba(0, 0, 0, 0.6)',
      },
    },
    defaultHover: {
      background: colorGrid.indigo7,
      borderColor: colorGrid.gray5,
    },
    inverted: {
      background: colorGrid.gray0,
      color: '#304DC5',
      borderColor: colorGrid.gray3,
    },
    invertedHover: {
      background: colorGrid.gray2,
    },
    splitter: {
      color: colorGrid.gray3,
    },
    iconButton: {
      background: '#6cdbe9',
      color: '#0f1c1e',
    },
  },
  selectInput: {
    color: 'rgba(0, 0, 0, 0.87)',
    caretColor: 'rgba(0, 0, 0, 0.6)',
    borderColor: colorGrid.gray3,
    background: '#304DC5',
    menuList: {
      background: colorGrid.gray0,
      borderColor: 'rgba(33, 33, 33, 0.06)',
      hovered: {
        background: 'rgba(42, 125, 187, 0.12)',
        color: 'rgba(0, 0, 0, 0.87)',
      },
    },
  },
  link: {
    color: '#3555D9',
  },
  input: {
    default: {
      color: 'rgba(0, 0, 0, 0.87)',
      hintColor: 'rgba(0, 0, 0, 0.6)',
      borderColor: 'rgba(0, 0, 0, 0.38)',
    },
    focused: {
      color: 'rgba(0, 0, 0, 0.87)',
      hintColor: '#3b5ff2',
      borderColor: '#304DC5',
    },
    error: {
      color: '#EA3D2F',
    },
  },
  searchInput: {
    borderColor: '#dadada',
    color: 'rgba(0, 0, 0, .6)',
    addBtn: {
      boxShadow: '0 4px 12px 1px #ccc',
    },
  },
  contacts: {
    searchInput: {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      borderFocusColor: '#304DC5'
    },
    contactLabel: {
      background: 'rgba(33, 33, 33, 0.06)',
      color: '#20272d',
      fontColor: colorGrid.gray9,
      subFontColor: 'rgba(0, 0, 0, 0.6)',
      icon: {
        fill: '#38b24d',
      },
      hover: {
        background: 'rgba(42, 125, 187, 0.12)',
      },
      arrow: {
        fill: 'rgba(0, 0, 0, 0.87)'
      },
    },
    buttons: {
      default: {
        container: {
          border: 'rgba(33, 33, 33, 0.06)',
        },
        save: {
          color: colorGrid.gray0,
          background: '#304DC5'
        },
        cancel: {
          color: '#304DC5',
          border: 'rgba(0, 0, 0, 0.12)'
        }
      }
    },
    link: {
      color: '#304DC5'
    }
  },
  checkbox: {
    background: colorGrid.gray0,
    borderColor: 'rgba(0, 0, 0, 0.6)',
    checked: {
      background: '#304DC5',
      borderColor: '#304DC5',
    },
  },
  icon: {
    fill: colorGrid.gray6,
    logoIcon: {
      fill: 'rgba(0, 0, 0, 0.6)',
    }
  },
  dialpad: {
    default: {
      background: colorGrid.gray0,
      borderColor: 'rgba(33, 33, 33, 0.06)',
      iconColor: 'rgba(0, 0, 0, 0.6)',
      color: 'rgba(0, 0, 0, 0.87)'
    },
    item: {
      background: 'rgba(10, 10, 10, 0.04)',
      color: '#304DC5',
      colorDisabled: colorGrid.gray9,
      colorActive: '#F28B82',
      backgroundHover: 'rgba(33, 33, 33, 0.06)',
      backgroundPressed: 'rgba(42, 125, 187, 0.12)',
      greenBackground: '#2f9e44',
      greenBackgroundHover: '#37B24D',
      redBackground: '#f24e41',
      redBackgroundHover: '#EA3D2F',
    },
    audioSettings: {
      heading: {
        color: colorGrid.gray9
      },
      button: {
        color1: 'rgba(0, 0, 0, 0.12)',
        color2: '#304DC5',
        color3: colorGrid.gray0,
        color4: '#304DC5'
      }
    },
    callDetail: {
      color1: 'rgba(0, 0, 0, 0.87)',
      color2: colorGrid.gray9
    },
    incomingCall: {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      color: '#304DC5',
      fontColor: colorGrid.gray9
    }
  },
  userControls: {
    color: 'rgba(0, 0, 0, 0.6)',
    defaultHover: {
      background: colorGrid.gray3,
    },
  },
  header: {
    background: '#1F3280',
    fill: colorGrid.gray0,
    color: colorGrid.gray0,
    hover: {
      color: colorGrid.gray2,
      fill: colorGrid.gray2,
    },
    subheader: {
      background: '#253C99',
    },
    button: {
      default: {
        background: colorGrid.gray0,
        color: colorGrid.gray5,
      },
      hover: {
        background: 'rgba(42, 125, 187, 0.12)',
      },
    },
    menuItem: {
      color: colorGrid.gray7,
      background: colorGrid.gray0,
      borderColor: colorGrid.gray3,

      hovered: {
        color: 'rgba(0, 0, 0, 0.87)',
        background: 'rgba(42, 125, 187, 0.12)',
      },
    },
  },
  alerts: {
    info: {
      background: '#daf2de',
      color: '#084c14'
    },
    warn: {
      background: '#FFE7E6',
      color: '#993D00'
    },
    error: {
      background: '#FFE7E6',
      color: '#EA3D2F'
    },
    listSection: {
      background: '#f2f2f2',
      borderColor: '#e5e5e5',
      markers: {
        info: {
          background: '#37B24D',
        },
        warn: {
          background: '#F3AA18',
        },
        error: {
          background: '#EA3D2F',
        },
      }
    }
  },
  history: {
    default: {
      color: 'rgba(0, 0, 0, 0.87)',
      hoverColor: '#1F3280',
      totalColor: 'rgba(0, 0, 0, 0.6)',
      borderColor: 'rgba(33, 33, 33, 0.06)',
      background: colorGrid.gray0,
      iconRegularColor: '#001221',
      iconErrorColor: colorGrid.red7,
    }
  }
};

window.__ALL_LOGS = new Set();
(function(){
  const originalConsoleMethods = {};
  const logFunction = (method) => {
    return function() {
      try {
        const args = Array.prototype.slice.call(arguments);

        if (window.__ALL_LOGS.size >= 50) {
          window.__ALL_LOGS = new Set([...window.__ALL_LOGS].reverse().slice(0, 49).reverse());
        }
        window.__ALL_LOGS.add(`${new Date()} <${method}> ${args.map(el => JSON.stringify(el)).join(' ')}`);
        originalConsoleMethods[method].apply(this, args);
      } catch(e) {
        originalConsoleMethods[method].apply(this, e);
        window.__ALL_LOGS.add(`${new Date()} <${method}> ${e.toString()}`);
      }

    };
  };

  ['log', 'info', 'warn'].forEach(functionName => {
    originalConsoleMethods[functionName] = console[functionName];
    console[functionName] = logFunction(functionName);
  });
})();

const MainContainer = styled.div`
  height: calc(100vh - 48px);
  position: relative;
`;

const IncomingCallsContainer = styled.div`
  z-index: 1000;
  position: absolute;
  top: 55px;
  width: 100%;
`;

const RetryButton = styled(Button)`
background-color: ${colorGrid.blue6};
  color: ${colorGrid.gray0};
  display : flex;
  height : 32px;
  justify-content : center;
  width : 80px;
  padding : 0px;
  margin-right : 4px;

  &:hover {
    background-color : ${colorGrid.blue4};
  }
`;

const MisConfigContainer = styled.div`
  display: flex;
  margin-top: 50px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SVGContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  width: 80px;
  border-radius: 50%;
  background-color: ${colorGrid.red1};
`;

const HeaderWithRouter = withRouter(Header);

const App = (props) => {
  const [auth, { setAuth, logout, updateIdentity, setIsOnline }] = useAuth();
  const [myLocale] = useLocale();
  const [alerts, { createAlert, createErrorAlert, removeSpecificAlert, clearPersistentAlerts, clearAlerts, createWarnAlert }] = useAlerts();
  const [settings, { changeSettings, setWSGLogs }] = useSettings();
  const [, , , { getContacts }] = useContacts();
  const [, { getCallHistory }] = useCallHistory();

  const isOnline = () => {
    return auth.isOnline;
  };

  // This state hook is way up here in the component tree because the click action of the 'addCall' button
  // causes a change in callInfo, which causes re-render of all child components.  Value of this 
  // is passed down as prop (prop drilled) Can this be dealt with more elegantly??
  const [showAddCallKeypad, setShowAddCallKeypad] = useState(false);

  // appStatus in appMenu will be set to Online iff both webSocket and internet connections are there
  // const [webSocketConnected, setWebSocketConnected] = useState('disconnected');
  const [isInternetConnected, setInternetConnected] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isWSOfflineAlertTriggered, setIsWSOfflineAlertTriggered] = useState(false);
  // Declare callInfo hook
  const [callInfo,
    { saveAudioSettings,
      setIncomingCall,
      hangupCall,
      sendCallToVoicemail,
      rejectCall,
      muteCall,
      answerCall,
      holdCall,
      handleDialPlanOptions,
      handleUpdateVoicemail,
      updateCallStatus,
      connectedCall,
      callCleanup,
    }] = useCallInfo();

  // set a flag to prevent duplicate calls to API
  let gettingVoicemailCount = false;

  const handleToggleAnswerHangup = () => {
    // If there is an active call, hang it up, if there incoming calls, answer the first one
    if (callInfo.activeCallId) {
      hangupCall(callInfo.activeCallId);
    } else if (Object.keys(callInfo.incomingCalls).length > 0) {
      answerCall(Object.keys(callInfo.incomingCalls)[0]);
    }
  };

  const handleToggleMute = () => {
    // console.log('HANDLE MUTE HEADSET CLICKED', callInfo.activeCallId);

    if (callInfo && callInfo.activeCallId) {
      muteCall(callInfo.activeCallId);
    }
  };

  const handleHangupAll = () => {
    if (callInfo.inprogressCalls) {
      Object.keys(callInfo.inprogressCalls).forEach(keyCallId => {
        hangupCall(keyCallId);
      });
    }

    const checkAndWaitForNoCalls = () => {
      if (Object.keys(callInfo.inprogressCalls).length > 0) {
        setTimeout(() => {
          checkAndWaitForNoCalls();
        }, 100);
      } else {
        // callback to container that all calls are disconnected
        if (typeof noCallsOKtoQuit == "function") {
          // eslint-disable-next-line no-undef
          noCallsOKtoQuit();
        } else if (typeof window["noCallsOKtoQuit"] == "function") {
          window.noCallsOKtoQuit();
        } else {
          console.error("^^^^^^^^^^^ NO noCallsOKtoQuit ???");
        }
      }
    };
    checkAndWaitForNoCalls();
  };

  // useEffect to run subcriptions for phone, but only once...
  useEffect(() => {
    const subId = AppService.getInstance().subscribe(async (event, status, data) => {
      console.log('"---> Subscription Event --->: ', event, status, data);
      switch (event) {
        case "app-status":
          // console.log('****************** APP STATUS update', event, status, data, auth);
          if (typeof data.status !== "undefined" && data.status === "connected") {
            clearAlerts('login');
            setIsOnline(true);
          } else if (typeof data.status !== "undefined" && data.status === "disconnected") {
            setIsOnline(false);
          } else if (status === "logout") {
            setIsOnline(false);
            setWSGLogs(null);
            logout();
            props.history.push(`/login`);
          }
          break;

        case "app-auth-data":
          setAuth(data);
          // change ui state to callprogress 
          props.history.push(`/callprogress`);
          break;

        case "online":
          setInternetConnected(true);
          break;

        case "offline":
          setInternetConnected(false);
          break;

        case "s2sCallService-early":
          // console.log(">>>>> Early Recieved ... calling set audio");
          saveAudioSettings(undefined);
          break;

        case "s2sCallService-progress":
          // console.log('inprogress call for id: ', data.id);
          break;

        case "s2sCallService-incoming-call":
          // necessary for electron app 
          if (typeof incomingCall == "function") {
            //eslint-disable-next-line no-undef
            incomingCall();
          } else if (typeof window["incomingCall"] == "function") {
            window.incomingCall();
          } else {
            console.log("^^^^ Browser Detected ^^^^");
          }

          setIncomingCall(data);
          break;

        case "s2sCallService-rejected":
          callCleanup(data.id);
          break;

        case "s2sCallService-connected":
          // console.warn('####### CONNECTED', data.id);

          // change status text to 'active' per design
          data.status = "active";
          data.userControlStatus = "active";

          updateCallStatus(data);
          connectedCall(data);
          break;

        case "s2sCallService-accepted":
          // console.log('TODO - handle call accepted?? ', data);

          break;

        case "s2sCallService-loggedIn":

          if (status === true) {

            // in old softphone, this would only update the app status. Do we
            // need to do this?  Can use an effect hook when we need to get app status
            // from appService
            //store.dispatch(doConnected(AppService.getInstance().getStatus(), appHistory));
          } else {
            props.history.push('/login');
          }
          break;

        case "s2sCallService-terminated":
          callCleanup(data.id);
          /** Check if there's any held call
            * If yes - switch to it.
          */
          // const heldCall = Object.values(callInfo.inprogressCalls).find(({ isHeld }) => isHeld);
          // if (typeof heldCall !== 'undefined') {
          //   holdCall(heldCall.id);
          // }

          break;

        case "s2sCallService-registered":
          // prevent rapid-fire events from spamming the api.
          setIsRegistered(true);
          changeSettings({ "isRegistered": true }, props.intl);
          if (!gettingVoicemailCount && isOnline) {
            gettingVoicemailCount = true;
            const response = await AppService.getInstance().speak("getVoicemailCount", { "extension_id": AppService.getInstance().getCallService().getCurrentExtension().extensionId });
            handleUpdateVoicemail({ "unread_count": response.count });
            gettingVoicemailCount = false;
          }
          clearPersistentAlerts();
          break;

        case "s2sCallService-unregistered":
        case "s2sCallService-registrationFailed":
          setIsRegistered(false);
          changeSettings({ "isRegistered": false }, props.intl);
          clearPersistentAlerts();
          if (data.cause && data.cause !== "logout") {
            createErrorAlert({
              message: getIntlText(props.intl, 'CALL-SERVICE-NOT-REGISTERED', 'Call service reporting not registered. Please check your network connection or restart the application and try again.'),
            });
          }
          // call cleanup 
          Object.keys(callInfo.inprogressCalls).forEach((callId) => {
            callCleanup(callId);
          });
          Object.keys(callInfo.incomingCalls).forEach((callId) => {
            callCleanup(callId);
          });
          break;

        case "showAlert":
          createAlert(data);
          break;

        case "alerts-new":
          createAlert(data);
          break;

        case 'websocket-message':
          //console.log('=====> Web Socket message', event, data);

          switch (data.data.event) {
            case 'identity_property_change':
              //console.log('dddddddd identity property Change detected', data, 'props', props, 'intl is:',  props.intl);
              updateIdentity();
              // identity change is caused by import of contacts so when this happens 
              // retrieve contacts so they get the latest ones.
              // must pass offset or it will concat
              getContacts(undefined, undefined, 0);
              break;

            case 'message_waiting_indicator':
              // console.log("message_waiting_indicator", data.data.event, data.data.metadata.unread_count);
              handleUpdateVoicemail(data.data.metadata);
              break;

            default:
              console.warn('<<< Unhandled \'websocket-message\' Event', status, data);
              break;
          }
          break;

        case "s2sCallService-dialPlanOptions":
          handleDialPlanOptions(AppService.getInstance().getConnectData().dialplan_options);
          break;

        case "s2sCallService-logging":
          // catching logs, listen to current event
          setWSGLogs(data);
          break;

        default:
          console.log("<<< Subscription Not Handled >>>", event);

      }
    });

    // // Necessary for electron app. СallInfo may not be updated yet.
    if (!callInfo.dialPlanOptions) {
      const dialplan_options = AppService.getInstance().getConnectData().dialplan_options;
      handleDialPlanOptions(dialplan_options);
    }

    // We tear down subscriptions when this component is unmounted.  Should only render once
    // when app is started... 

    return () => {
      // console.log('tearing it down ', subId);
      AppService.getInstance().removeSubscription(subId);
    };
  }, [callInfo]);

  // useEffect to setup subscriptions to headset events.  Need to refresh with each render so 
  // we are sure to have udpated callInfo, etc.  Return function removes the listeners to avoid
  // memory leak.
  useEffect(() => {

    // **********************************
    // **** Headset Event Listeners  ****
    // **********************************

    // TA - browser now handles sleep, resume, and usb device changes

    // This event is fired when the electron window is closing. Need to hangup
    // all calls before window closes so that call service is not left in bad state
    document.addEventListener("hangupAll", handleHangupAll, false);

    // This event is fired when the answer/hangup button is pressed on the headset 
    document.addEventListener("toggleAnswerHangup", handleToggleAnswerHangup, false);

    // This event is fired when mute button is pressed on Plantronics headset
    document.addEventListener("toggleMute", handleToggleMute, false);

    return () => {
      document.removeEventListener('toggleAnswerHangup', handleToggleAnswerHangup);
      document.removeEventListener("hangupAll", handleHangupAll);
      document.removeEventListener("toggleMute", handleToggleMute);
    };
  }, [callInfo]);

  useEffect(() => {
    // Display Message when Websocket Gateway is Offline
    if (isRegistered && !auth.isOnline && auth.isAuthorized) {
      // Display message once per disconnection
      if (!isWSOfflineAlertTriggered) {
        createWarnAlert({
          message: getIntlText(props.intl, 'WEBSOCKET-PERFORMING-CONNECTION-ALERT-HEADING', "We are having issues performing this action. Please, try again in a few moments."),
          autoCloseTimeout: 4,
        });
        setIsWSOfflineAlertTriggered(true);
      }
    } else {
      setIsWSOfflineAlertTriggered(false);
    }

    return () => {
      document.removeEventListener('toggleAnswerHangup', handleToggleAnswerHangup);
      document.removeEventListener("hangupAll", handleHangupAll);
      document.removeEventListener("toggleMute", handleToggleMute);
    };
  }, [callInfo]);

  useEffect(() => {
    const getAudio = async () => {
      // First I must see my list of devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filtering input devices (microphones)
      const inputAudio = devices.filter((device) => {
        return device.kind === "audioinput"; // returns a InputDeviceInfo object which is read only
      }).map((device) => {
        // InputDeviceInfo is a read only object so I must create a new object so it can be successfully merged later on change
        return {
          "value": device.deviceId,
          "label": device.label
        };
      });

      // Filtering output devices (speakers)
      const outputAudio = devices.filter((device) => {
        return device.kind === "audiooutput"; // returns a MediaDeviceInfo object which is read-only
      }).map((device) => {
        // MediaDeviceInfo is a read only object so I must create a new object so it can be successfully merged later on change
        return {
          "value": device.deviceId,
          "label": device.label
        };
      });

      // if my selected devices do not exist in the list of devices
      if (outputAudio.indexOf(settings.selectedSpeaker) === -1 || inputAudio.indexOf(settings.selectedAudio) === -1) {
        changeSettings({
          "selectedSpeaker": outputAudio[0],
          "selectedAudio": inputAudio[0]
        });
      }

    };

    if (navigator.mediaDevices !== null && navigator.mediaDevices !== undefined) {
      navigator.mediaDevices.ondevicechange = getAudio;
    }

    return () => {
      navigator.mediaDevices.ondevicechange = undefined;
    };
  }, [settings]);

  useEffect(() => {
    // check appService for local storage setting
    if(typeof settings.advancedLogging === "undefined"){
      changeSettings({"advancedLogging": AppService.getInstance().advancedLogging});
    }
    // Listen to switching advanced logging
    AppService.getInstance().advancedLogging = settings.advancedLogging;
  }, [settings]);

  const dismissAlert = (alertId) => {
    removeSpecificAlert(alertId);
    //AlertService.getInstance().removeSpecificAlert(alertId);
  };

  const callHandleUserAction = (action, data) => {
    //console.log('callHandleUserAction -- App route', action, data);

    // Update callHistory Caching after call handle. 
    getCallHistory();

    // These user action handlers are for the incoming call component
    switch (action.toLowerCase()) {
      case 'hangup':
        // data is expected to be callId
        // props.dispatch(doHangUp(data));
        hangupCall(data.callId);
        break;

      case 'incoming-accept':
        // data is expected to be callId
        // props.dispatch(doAnswer(data));
        answerCall(data.callId);
        // set route to callprogress
        props.history.push(`/callprogress`);
        break;

      case 'incoming-decline':
        // data is expected to be callId
        // props.dispatch(doRejectCall(data));
        rejectCall(data.callId);
        break;

      case 'incoming-to-voicemail':
        // need Verto service to do this....
        // data is expected to be callId
        sendCallToVoicemail(data.callId);
        // props.dispatch(doSendToVoicemail(data));
        break;

      default:
        console.log('Need to handle this button click', action, data);

    }
    // should be answer, ignore, or voicemail...
    // TODO call main handle user action function
  };

  const handleHeaderButtonClick = (data) => {
    // console.log('HandleHeaderClick', props, data);

    switch (data) {
      case 'contactlist':
        props.history.push('/contacts');
        break;
      case 'back':
        props.history.goBack();
        break;
      default:
        console.warn('HandleHeaderButton click value not handled', data);
    }
  };

  const needsHeader = (props) => {

    const routeNeedsHeader = (currentPath) => {
      const noHeaderRoutes = ["/update", "/username", "/login", "/help", "/misconfig", "/forgotpw"];
      let bReturn = noHeaderRoutes.reduce((p, c) => {
        if (p) {
          if (currentPath.indexOf(c) > -1) {
            return false;
          }
        }
        return p;
      }, true);
      if (currentPath.length === 1 && currentPath === "/") {
        bReturn = false;
      }
      //console.log('routeNeedsHeader returning: ', bReturn, currentPath);

      return bReturn;
    };
    return routeNeedsHeader(props.location.pathname);
  };

  const renderHeader = () => {
    //console.log('rrrrrrr header', props, auth);
    if (!needsHeader(props)) {
      return (<React.Fragment />);
    } else {
      const availableExtensions = AppService.getInstance().getCallService().getAvailableExtensions();
      return (
        <HeaderWithRouter
          status={isInternetConnected && isRegistered ? 'connected' : 'offline'}
          cbChangeRoute={handleHeaderButtonClick}
          availableExtensions={availableExtensions}
        />
      );
    }
  };

  const renderIncomingCall = () => {

    //const currentExtension = AppService.getInstance().getCallService().getCurrentExtension();

    return callInfo && Object.keys(callInfo.incomingCalls).map((callId) => {

      // const defaultExtension = settings.extensions.filter((e) => {
      //   return e.extension === settings.selectedExtension;
      // })[0];
      // console.log('xxxxxxx', callInfo.incomingCalls[callId]);
      return (
        <IncomingCall
          callDetails={callInfo.incomingCalls[callId]}
          cbOnClick={callHandleUserAction}
          key={callId}
          vmEnabled={true} //{defaultExtension.vm}  //this is a temporary as we test, we should always be authorized when we have an incoming call

        />
      );
    });
  };

  const renderIncomingCallsContainer = () => {

    // console.log('callInfo.incomingCalls.length', callInfo.incomingCalls.length)
    if (Object.keys(callInfo.incomingCalls).length > 0) {
      return (
        <IncomingCallsContainer className="incomingCallsContainer">
          {renderIncomingCall()}
        </IncomingCallsContainer>
      );
    } else {
      return undefined;
    }

  };

  const goBack = () => {
    props.history.goBack();
  };

  const CallProgressWithRouter = withRouter(CallProgress);

  // console.warn(" ##### webSocketConnected, internetConnected", internetConnected);

  const themeMode = {
    ...(settings.darktheme ? THEME_DARK : THEME_LIGHT),
    isDarkTheme: settings.darktheme,
  };

  return (
    <IntlProvider
      key={myLocale.locale}
      locale={myLocale.locale}
      messages={myLocale.messages}
    >
      <ThemeProvider theme={themeMode}>
        <DocumentTitle>
          <AlertList
            alertArray={alerts ? alerts.activeAlerts : undefined}
            cbDismissAlert={dismissAlert}
          />
          {renderHeader()}
          {renderIncomingCallsContainer()}
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/connect" component={Connect} />
            <Route path="/forgotpw" component={ForgotPw} />
            <Route path="/changepwd" component={ChangePassword} />
            <Route path="/update" component={Update} />
            <Route path="/help" component={Help} />
            <PrivateRoute path="/settings/appearance" component={() => <SettingsNav title={getIntlText(props.intl, 'SETTINGS-APPEARANCE-BTN-LABEL', "Appearance")} cbBackClick={goBack} ><AppearanceSettings /></SettingsNav>} />
            <PrivateRoute path="/settings/notification" component={() => <SettingsNav title={getIntlText(props.intl, 'SETTINGS-NOTIFICATIONS-BTN-LABEL', "Notifications")} cbBackClick={goBack} ><NotificationSettings /></SettingsNav>} />
            <PrivateRoute path="/settings/devices" component={() => <SettingsNav title={getIntlText(props.intl, 'SETTINGS-DEVICES-BTN-LABEL', "Devices")} cbBackClick={goBack} ><DeviceSettings /></SettingsNav>} />
            <PrivateRoute path="/settings/contacts" component={() => <SettingsNav title={getIntlText(props.intl, 'SETTINGS-CONTACTS-BTN-LABEL', "Contacts")} cbBackClick={goBack} ><ContactSettings /></SettingsNav>} />
            <PrivateRoute path="/settings/troubleshooting" component={() => <SettingsNav title={getIntlText(props.intl, 'SETTINGS-TROUBLESHOOTING-BTN-LABEL', "Troubleshooting")} cbBackClick={goBack} ><TroubleshootingSettings history={props.history}/></SettingsNav>} />
            <PrivateRoute path="/settings/:section" component={TempComponent} />
            <PrivateRoute path="/settings" component={Settings} />
            <PrivateRoute path="/alerts" component={Alerts}/>
            <PrivateRoute exact path="/contacts" component={Contacts} />
            <PrivateRoute exact path="/contacts/:id" component={Contact} />
            <PrivateRoute exact path="/contacts/:id/edit" component={ContactEdit} />
            <PrivateRoute exact path="/callprogress" component={() => <CallProgressWithRouter setShowAddCallKeypad={setShowAddCallKeypad} showAddCallKeypad={showAddCallKeypad} />} />
            <PrivateRoute path="/history" component={History} />
            <Route path="/misconfig" component={MisConfig} />
            <Route path="/" component={Login} />
          </Switch>
        </DocumentTitle>
      </ThemeProvider>
    </IntlProvider >
  );
};

App.propTypes = {
  'history': PropTypes.object,
  'intl': PropTypes.object
};

const TempComponent = injectIntl(({ ...rest }) => {
  const [myLocale, { setDialect }] = useLocale();

  const loc = rest.location.pathname;//rest.location.pathname;
  return (<div>
    <h2>Temp React Component</h2>
    <div>{loc} - locale is: {myLocale.locale}</div>
    <div><button onClick={() => {
      const newDialect = myLocale.locale === "en" ? "es" : "en";
      setDialect(newDialect);
      //dispatch({ type: 'newDialect', "dialect": newDialect });

    }}>switch between en or es</button></div>
  </div>);
});

const DocumentTitle = withRouter(({ ...rest }) => {
  //console.log('hhhhhh', document.title, rest);
  let loc = rest && rest.location && rest.location.pathname.substr(1);
  if (loc && loc.indexOf('/') > -1) {
    loc = loc.substr(0, loc.indexOf('/'));
  }
  if (loc && loc.length <= 1) {
    loc = 'login';
  }
  document.title = loc && loc.length > 0 ? `${loc} - ${"StarPhone"}` : `${"StarPhone"}`;
  return (
    <MainContainer className="MainContainer">
      {rest.children}
    </MainContainer>
  );
});

const PrivateRoute = ({ "component": Component, ...rest }) => {
  const [auth] = useAuth();

  const isAuthorized = auth && auth.isAuthorized;

  //console.log('>>>>>>>>>> private route', auth, isAuthorized);
  return (
    <Route
      {...rest}
      render={(props) => {
        //console.log('>>>> private route', props);
        return isAuthorized ? (
          <Component {...props} />
        ) : (
            <Redirect
              to={{
                pathname: "/",
                state: { from: props.location }
              }}
            />
          );
      }

      }
    />
  );
};

PrivateRoute.propTypes = {
  component: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object
};

const MisConfig = (props) => {

  const retryClick = () => {
    props.history.push('./login');
  };
  return (<MisConfigContainer>
    <SVGContainer>
      <AlertIconSVG svgStyle={{ height: "60px", width: "60px", fill: "red" }} />
    </SVGContainer>
    <h4>{Utilities.getInstance().getIntlMessage('MISCONFIG-NO-EXTENSIONS', "No extensions found.")}</h4>
    <RetryButton
      buttonLabel={Utilities.getInstance().getIntlMessage('RETRY-BTN-LABEL', "Retry")}
      buttonSize="large"
      buttonType="secondary"
      cbClick={retryClick}
    />
    <h4>{Utilities.getInstance().getIntlMessage('MISCONFIG-SUPPORT', "Please contact your support organization.")}</h4>

  </MisConfigContainer>);
};

MisConfig.propTypes = {
  match: PropTypes.object
};

export default withRouter(App);
